import React, { useMemo } from "react";

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Table, Tooltip } from "antd";
import moment from "moment";

import "./SwabListTable.css";

const EllipsisCell = ({ value, tooltipClassName }) => (
  <Tooltip title={value} placement="topLeft" classNames={{ root: tooltipClassName }}>
    <span className="SwabListTable__Ellipsis">{value}</span>
  </Tooltip>
);

export const FinalResultCell = ({ value }) => {
  if (value === "In Spec") {
    return (
      <span className="SwabListTable__FinalResult SwabListTable__FinalResult--inSpec">
        <CheckCircleOutlined aria-hidden />
        In Spec
      </span>
    );
  }

  if (value === "Out of Spec") {
    return (
      <span className="SwabListTable__FinalResult SwabListTable__FinalResult--outOfSpec">
        <CloseCircleOutlined aria-hidden />
        Out of Spec
      </span>
    );
  }

  if (value === "In Progress" || value === "In Process") {
    return (
      <span className="SwabListTable__FinalResult SwabListTable__FinalResult--inProgress">
        <span className="SwabListTable__FinalResultDot" aria-hidden />
        {value}
      </span>
    );
  }

  return <span className="SwabListTable__FinalResult">{value || "-"}</span>;
};

export const formatSwabReceivedDate = (date) => (
  date ? moment(date, ["YYYY-MM-DD", "DD/MM/YYYY"], true).format("DD/MM/YYYY") : "-"
);

const SwabListTable = ({
  data = [],
  testsColumnLabel = "Tests",
  includeReceivedDate = true,
  includeTests = true,
  includeFinalResult = false,
  tooltipClassName = "SwabListTable__TooltipOverlay",
  scrollY = 360,
  tableClassName = "",
  pagination = false,
}) => {
  const columns = useMemo(() => {
    const cols = [
      {
        title: "Sample ID",
        dataIndex: "sample_id",
        key: "sample_id",
        width: includeFinalResult && !includeTests ? "25%" : "32%",
        ellipsis: true,
        render: (value) => (
          <EllipsisCell value={value} tooltipClassName={tooltipClassName} />
        ),
      },
    ];

    if (includeReceivedDate) {
      cols.push({
        title: "Received date",
        dataIndex: "received_date",
        key: "received_date",
        width: "25%",
        render: (value) => formatSwabReceivedDate(value),
      });
    }

    cols.push({
      title: "Sample Type",
      dataIndex: "sample_type",
      key: "sample_type",
      width: includeReceivedDate ? "25%" : "34%",
      className: "SwabListTable__Cell--coolDark",
    });

    if (includeTests) {
      cols.push({
        title: testsColumnLabel,
        dataIndex: "tests",
        key: "tests",
        ellipsis: true,
        className: "SwabListTable__Cell--coolDark",
        render: (value) => (
          <EllipsisCell value={value} tooltipClassName={tooltipClassName} />
        ),
      });
    }

    if (includeFinalResult) {
      cols.push({
        title: "Final Result",
        dataIndex: "final_result",
        key: "final_result",
        width: "20%",
        render: (value) => <FinalResultCell value={value} />,
      });
    }

    return cols;
  }, [
    includeReceivedDate,
    includeTests,
    includeFinalResult,
    testsColumnLabel,
    tooltipClassName,
  ]);

  const dataSource = useMemo(
    () => data.map((row, index) => ({
      ...row,
      key: `${row.sample_id}-${index}`,
    })),
    [data],
  );

  return (
    <div className="SwabListTable__Card">
      <Table
        className={`SwabListTable ${tableClassName}`.trim()}
        columns={columns}
        dataSource={dataSource}
        pagination={pagination}
        scroll={scrollY ? { y: scrollY } : undefined}
        size="small"
      />
    </div>
  );
};

export default SwabListTable;
